import { API_BASE_URL, API_TIMEOUT } from '@config/constants';
import type { ApiResponse, ApiError } from '@appTypes/common.types';

export interface RequestConfig {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  timeout?: number;
  signal?: AbortSignal;
}

type RequestInterceptor = (config: RequestConfig) => RequestConfig | Promise<RequestConfig>;
type ResponseInterceptor = (response: Response) => Response | Promise<Response>;
type ErrorInterceptor = (error: ApiError) => ApiError | Promise<ApiError>;

class HttpClient {
  private baseURL: string;
  private timeout: number;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];

  constructor(baseURL: string = API_BASE_URL, timeout: number = API_TIMEOUT) {
    this.baseURL = baseURL;
    this.timeout = timeout;
  }

  useRequestInterceptor(interceptor: RequestInterceptor): () => void {
    this.requestInterceptors.push(interceptor);
    return () => {
      this.requestInterceptors = this.requestInterceptors.filter((i) => i !== interceptor);
    };
  }

  useResponseInterceptor(interceptor: ResponseInterceptor): () => void {
    this.responseInterceptors.push(interceptor);
    return () => {
      this.responseInterceptors = this.responseInterceptors.filter((i) => i !== interceptor);
    };
  }

  useErrorInterceptor(interceptor: ErrorInterceptor): () => void {
    this.errorInterceptors.push(interceptor);
    return () => {
      this.errorInterceptors = this.errorInterceptors.filter((i) => i !== interceptor);
    };
  }

  private buildURL(url: string, params?: RequestConfig['params']): string {
    const fullURL = url.startsWith('http') ? url : `${this.baseURL}${url}`;
    if (!params) return fullURL;

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${fullURL}?${queryString}` : fullURL;
  }

  async request<T>(config: RequestConfig): Promise<ApiResponse<T>> {
    let processedConfig = { ...config };

    for (const interceptor of this.requestInterceptors) {
      processedConfig = await interceptor(processedConfig);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), processedConfig.timeout ?? this.timeout);

    try {
      const url = this.buildURL(processedConfig.url, processedConfig.params);
      const fetchOptions: RequestInit = {
        method: processedConfig.method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...processedConfig.headers,
        },
        signal: processedConfig.signal ?? controller.signal,
      };

      if (processedConfig.data && !['GET', 'DELETE'].includes(processedConfig.method)) {
        fetchOptions.body = JSON.stringify(processedConfig.data);
      }

      let response = await fetch(url, fetchOptions);

      for (const interceptor of this.responseInterceptors) {
        response = await interceptor(response);
      }

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const apiError: ApiError = {
          status: response.status,
          message: errorBody.message || response.statusText || 'Request failed',
          code: errorBody.code,
          details: errorBody.details,
          requestPath: processedConfig.url,
        };

        for (const interceptor of this.errorInterceptors) {
          await interceptor(apiError);
        }

        throw apiError;
      }

      const data = await response.json();
      return { data, status: response.status, message: 'Success' };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  get<T>(url: string, params?: RequestConfig['params']): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: 'GET', params });
  }

  post<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: 'POST', data });
  }

  put<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: 'PUT', data });
  }

  patch<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: 'PATCH', data });
  }

  delete<T>(url: string): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: 'DELETE' });
  }
}

export const httpClient = new HttpClient();
export default HttpClient;
